/**
 * Frontend key cache with TTL to minimize IPC round-trips to Rust.
 *
 * Reads are served from cache unless TTL expired, then a single IPC to Rust.
 * Writes always go through Rust, then update the cache immediately (write-through).
 */

import { getRawKey, storeApiKey, deleteApiKey } from "$lib/api/invoke";

declare global {
  interface Window {
    __TAURI_INTERNALS__?: { metadata?: unknown };
  }
}

const TTL_MS = 5 * 60 * 1000; // 5 minute TTL

/** Provider priority order — browser-compatible providers first.
 * Claude's API blocks CORS in browsers; it only works in Tauri WebView. */
const isBrowserMode = typeof window !== "undefined" && !window.__TAURI_INTERNALS__?.metadata;
const PROVIDERS_BROWSER = [
  "gemini",
  "fireworks",
  "deepseek",
  "together",
  "openai",
  "claude",
] as const;
const PROVIDERS_TAURI = [
  "gemini",
  "claude",
  "openai",
  "fireworks",
  "deepseek",
  "together",
] as const;
const PROVIDERS = isBrowserMode ? PROVIDERS_BROWSER : PROVIDERS_TAURI;

interface CachedKey {
  raw: string | null;
  provider: string;
  cachedAt: number;
}

class KeyCache {
  private cache = new Map<string, CachedKey>();

  /** Get the raw API key for a provider (cached with TTL). */
  async get(provider: string): Promise<string | null> {
    const cached = this.cache.get(provider);
    if (cached && Date.now() - cached.cachedAt < TTL_MS) {
      return cached.raw;
    }
    const raw = await getRawKey(provider);
    this.cache.set(provider, { raw, provider, cachedAt: Date.now() });
    return raw;
  }

  /** Invalidate a single provider's cache entry. */
  invalidate(provider: string): void {
    this.cache.delete(provider);
  }

  /** Check if any provider has a valid key configured. */
  async hasAnyKey(): Promise<boolean> {
    for (const provider of PROVIDERS) {
      const key = await this.get(provider);
      if (key) return true;
    }
    return false;
  }

  /** Get the first available provider and its key. */
  async getFirstAvailable(): Promise<{ provider: string; key: string } | null> {
    for (const provider of PROVIDERS) {
      const key = await this.get(provider);
      if (key) return { provider, key };
    }
    return null;
  }

  /** Store a key via Rust and update local cache (write-through). */
  async store(provider: string, key: string): Promise<void> {
    await storeApiKey(provider, key);
    this.cache.set(provider, { raw: key, provider, cachedAt: Date.now() });
  }

  /** Delete a key via Rust and invalidate cache. */
  async delete(provider: string): Promise<void> {
    await deleteApiKey(provider);
    this.cache.delete(provider);
  }

  /** Clear all cached entries so the next read re-fetches from Rust/mock. */
  clearAll(): void {
    this.cache.clear();
  }
}

export const keyCache = new KeyCache();
